import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import type {
  ConversationStartersContextPriority,
  ConversationStartersContextWriter,
} from '@core/tellent-assistant-contracts';
import type { ConversationStartersContext } from '@core/tellent-assistant-types';

const PRIORITY_ORDER: Record<ConversationStartersContextPriority, number> = { page: 1, overlay: 2 };

interface ContextEntry {
  context: ConversationStartersContext;
  priority: ConversationStartersContextPriority;
}

/** Top context wins: overlays outrank pages, ties go to the latest push. */
@Injectable({ providedIn: 'root' })
export class AssistantStartersContextService implements ConversationStartersContextWriter {
  private readonly entries = new BehaviorSubject<readonly ContextEntry[]>([]);

  public readonly context$: Observable<ConversationStartersContext | null> = this.entries.pipe(
    map(entries =>
      entries.length
        ? entries.reduce((top, entry) =>
            PRIORITY_ORDER[entry.priority] >= PRIORITY_ORDER[top.priority] ? entry : top,
          ).context
        : null,
    ),
    distinctUntilChanged(),
  );

  public push(
    context: ConversationStartersContext,
    priority: ConversationStartersContextPriority,
  ): () => void {
    const entry: ContextEntry = { context, priority };
    this.entries.next([...this.entries.value, entry]);
    return () => this.entries.next(this.entries.value.filter(e => e !== entry));
  }
}
